import { useMutation } from '@apollo/client';
import { InsertConversationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { InsertConversationTRPCInput } from './useInsertConversationTRPC';
import { useInsertConversationTRPC } from './useInsertConversationTRPC';

/**
 * Flag-gated wrapper (RSP-6496). Both paths return `{ Id }` so
 * `useComments.tinycomments_create` reads `.Id` regardless of which ran.
 */
export const useInsertConversation = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertConversationGraphQL, graphqlState] = useMutation(
    InsertConversationDocument
  );

  const trpcMutation = useInsertConversationTRPC();

  const insertConversation = async (
    input: InsertConversationTRPCInput
  ): Promise<{ Id: string }> => {
    if (trpcEnabled) {
      return trpcMutation.insertConversation(input);
    }

    // The Apollo `insertConversation` doc requires a non-null `ParentId`
    // (`uuid!`). Consumers guard on it before calling (see
    // `useComments.tinycomments_create`), so this narrows the optional wrapper
    // input to the doc's required variable without a cast.
    if (!input.ParentId) {
      throw new Error('Cannot insert conversation without a ParentId');
    }

    const result = await insertConversationGraphQL({
      variables: {
        Content: input.Content,
        ParentId: input.ParentId,
      },
    });
    if (!result.data?.insert_conversation_one) {
      throw new Error('Failed to insert conversation');
    }

    return { Id: result.data.insert_conversation_one.Id };
  };

  if (trpcEnabled) {
    return {
      insertConversation,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertConversation,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
